import { AloftArea, SubjectData } from '@aloft/toolkit-shared';
import { Block, Button, Flex, Icon, Modal, Typography } from '@aloft/toolkit-sui';

import { useState } from 'react';

import { useZodFormContext } from '../../../../hooks/useZodForm.ts';
import { FiltersMessage, RecommendCompsFormSchema } from '../../types.ts';
import { BooleanTriStateFilter } from './BooleanTriStateFilter.tsx';
import { MinMaxButtonFilter } from './MinMaxButtonFilter.tsx';
import { PercentageAreaFilter } from './PercentageAreaFilter.tsx';
import { PropertyTypeFilter } from './PropertyTypeFilter.tsx';
import { SaleDateFilter } from './SaleDateFilter.tsx';
import { YearBuiltFilter } from './YearBuiltFilter.tsx';

type FilterPanelProps = {
  subject: SubjectData;
  isLoading: boolean;
  allowApplyNewFilters: boolean;
  orderID: string;
  filtersMessage?: FiltersMessage;
  onCancel: () => void;
  onApply: () => void;
};

const toAloftArea = (area?: { unitAmount: number; unit: 'sqft' | 'acre' }) =>
  area ? AloftArea.fromNumber(area.unitAmount, area.unit) : undefined;

export const FilterPanel = ({
  subject,
  isLoading,
  allowApplyNewFilters,
  filtersMessage,
  onCancel,
  onApply,
}: FilterPanelProps) => {
  const [isCloseWarningModalOpen, setIsCloseWarningModalOpen] = useState(false);
  const { trigger } = useZodFormContext<RecommendCompsFormSchema>();

  const { improvements, site } = subject.editableData;
  const listing = subject.readonlyData.backupListingData?.listing;

  const subjectGLA = toAloftArea(improvements.gla);
  const subjectLotSize = toAloftArea(site.area);

  const handleCancel = () => {
    if (allowApplyNewFilters) {
      setIsCloseWarningModalOpen(true);
    } else {
      onCancel();
    }
  };

  const handleApply = () => {
    void trigger('search.filters').then((filtersAreValid) => {
      if (filtersAreValid) {
        onApply();
      }
    });
  };

  return (
    <Flex flexDirection="column" height="100%" width="320px" minWidth="320px" flexShrink={0} backgroundColor="surface1">
      <Modal
        isOpen={isCloseWarningModalOpen}
        onClose={() => setIsCloseWarningModalOpen(false)}
        title="Unapplied filter changes"
        primaryAction={{
          text: 'Apply',
          onClick: () => {
            handleApply();
            setIsCloseWarningModalOpen(false);
          },
        }}
        secondaryAction={{
          text: "Don't apply",
          onClick: () => {
            onCancel();
            setIsCloseWarningModalOpen(false);
          },
        }}
        closeOnEsc={false}
        showCloseButton={false}
      >
        <Typography variant="body1">
          You&apos;ve updated the filters, but those changes haven&apos;t been applied yet. Do you
          want to apply them before closing?
        </Typography>
      </Modal>
      <Flex
        flex={1}
        overflowY="auto"
        overflowX="hidden"
        flexDirection="column"
        gap="24px"
        padding="24px"
        minWidth={0}
      >
        <PropertyTypeFilter />
        <SaleDateFilter />
        <PercentageAreaFilter label="GLA" fieldName="gla" subjectArea={subjectGLA} />
        <PercentageAreaFilter label="Site Size" fieldName="lotSize" subjectArea={subjectLotSize} />
        <YearBuiltFilter subjectYearBuilt={listing?.YearBuilt} />
        <MinMaxButtonFilter
          label="Quality"
          fieldName="quality"
          options={[1, 2, 3, 4, 5, 6]}
          formatLabel={(rating) => `Q${rating}`}
        />
        <MinMaxButtonFilter
          label="Condition"
          fieldName="condition"
          options={[1, 2, 3, 4, 5, 6]}
          formatLabel={(rating) => `C${rating}`}
        />
        <MinMaxButtonFilter
          label="Bedrooms"
          fieldName="bedrooms"
          options={[1, 2, 3, 4, 5, 6]}
          hasPlus
        />
        <MinMaxButtonFilter
          label="Baths: Full"
          fieldName="fullBaths"
          options={[1, 2, 3, 4, 5]}
          hasPlus
        />
        <MinMaxButtonFilter
          label="Baths: Half"
          fieldName="halfBaths"
          options={[1, 2, 3]}
          hasPlus
        />
        <MinMaxButtonFilter
          label="Garage Spaces"
          fieldName="garageSpaces"
          options={[1, 2, 3]}
          hasPlus
        />
        <BooleanTriStateFilter label="Basement" fieldName="hasBasement" />
        <BooleanTriStateFilter label="Pool" fieldName="hasPool" />
      </Flex>
      <Flex
        flexDirection="column"
        gap="8px"
        borderTopWidth="1px"
        borderStyle="solid"
        borderColor="strokeDefault"
        padding="16px 24px"
      >
        <Flex gap="16px">
          <Block flex={1}>
            <Button
              variant="secondary"
              text="Cancel"
              size="sm"
              fullWidth
              onClick={handleCancel}
            />
          </Block>
          <Block flex={1}>
            <Button
              text="Apply"
              size="sm"
              fullWidth
              disabled={!allowApplyNewFilters || isLoading}
              onClick={handleApply}
            />
          </Block>
        </Flex>
        {filtersMessage && (
          <Flex alignItems="center" gap="8px">
            <Icon color={filtersMessage.color} icon={filtersMessage.icon} size="sm" />
            <Typography variant="body2" color="textSecondary">
              {filtersMessage.message}
            </Typography>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
